import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, MapPin, CalendarDays, Users, Check, X, ShieldCheck, Star, CheckCircle2, Navigation, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { EVENT_CATEGORIES, EVENT_STATUS } from '../lib/eventData';
import MiniLocationMap from '../components/MiniLocationMap';
import EventReviewDialog from '../components/EventReviewDialog';
import AddToCalendar from '../components/AddToCalendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_STATUS = {
  pending: { lv: 'Gaida', en: 'Pending', ru: 'Ожидает', cls: 'bg-[#F3F2EE] text-[#5C5C5C]' },
  accepted: { lv: 'Apstiprināts', en: 'Accepted', ru: 'Принято', cls: 'bg-green-100 text-[#4A6B53]' },
  rejected: { lv: 'Noraidīts', en: 'Rejected', ru: 'Отклонено', cls: 'bg-red-100 text-red-600' },
};

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myApps, setMyApps] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applySlot, setApplySlot] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [myReviewees, setMyReviewees] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [attending, setAttending] = useState(false);

  const isOrganizer = user && event && event.organizer_id === user._id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/events/${id}`, { withCredentials: true });
      setEvent(data);
      try {
        const att = await axios.get(`${API}/events/${id}/attendance`, { withCredentials: true });
        setAttending(!!att.data?.attending);
      } catch (e) { /* ignore */ }
      try {
        const rev = await axios.get(`${API}/events/${id}/reviews`, { withCredentials: true });
        setReviews(rev.data);
      } catch (e) { /* ignore */ }
      if (user) {
        try {
          const mine = await axios.get(`${API}/events/${id}/my-applications`, { withCredentials: true });
          setMyApps(mine.data);
        } catch (e) { /* ignore */ }
        try {
          const mr = await axios.get(`${API}/events/${id}/my-reviews`, { withCredentials: true });
          setMyReviewees(mr.data || []);
        } catch (e) { /* ignore */ }
        if (data.organizer_id === user._id) {
          try {
            const apps = await axios.get(`${API}/events/${id}/applications`, { withCredentials: true });
            setApplications(apps.data);
          } catch (e) { /* ignore */ }
        }
      }
    } catch (e) { setEvent(null); } finally { setLoading(false); }
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  const openApply = (slot) => {
    if (!user) { navigate('/login'); return; }
    setApplySlot(slot); setMessage(''); setApplyOpen(true);
  };

  const submitApply = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/events/${id}/apply`, { slot_id: applySlot.id, message: message || null }, { withCredentials: true });
      toast.success(tr('Pieteikums nosūtīts!', 'Application sent!', 'Заявка отправлена!'));
      setApplyOpen(false);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : tr('Neizdevās', 'Failed', 'Ошибка'));
    } finally { setBusy(false); }
  };

  const decide = async (appId, accept) => {
    try {
      await axios.post(`${API}/events/${id}/applications/${appId}/${accept ? 'accept' : 'reject'}`, {}, { withCredentials: true });
      toast.success(accept ? tr('Apstiprināts', 'Accepted', 'Принято') : tr('Noraidīts', 'Rejected', 'Отклонено'));
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : tr('Neizdevās', 'Failed', 'Ошибка'));
    }
  };

  const completeEvent = async () => {
    if (!window.confirm(tr('Pabeigt pasākumu? Dalībnieki varēs atstāt atsauksmes.', 'Complete event? Participants will be able to leave reviews.', 'Завершить событие?'))) return;
    setBusy(true);
    try {
      await axios.post(`${API}/events/${id}/complete`, {}, { withCredentials: true });
      toast.success(tr('Pasākums pabeigts!', 'Event completed!', 'Событие завершено!'));
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : tr('Neizdevās', 'Failed', 'Ошибка'));
    } finally { setBusy(false); }
  };

  const openReview = (targetId, targetName) => {
    setReviewTarget({ id: targetId, name: targetName });
    setReviewOpen(true);
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#4A6B53]" /></div>;
  if (!event) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
    <div className="text-center"><p className="text-[#5C5C5C] mb-4">{tr('Pasākums nav atrasts.', 'Event not found.', 'Не найдено.')}</p>
    <Link to="/events"><Button variant="outline">{tr('Atpakaļ', 'Back', 'Назад')}</Button></Link></div></div>;

  const cat = EVENT_CATEGORIES[event.category];
  const status = EVENT_STATUS[event.status] || EVENT_STATUS.open;
  const myAppFor = (slotId) => myApps.find((a) => a.slot_id === slotId);
  const iAmAccepted = myApps.some((a) => a.status === 'accepted');
  const acceptedApps = applications.filter((a) => a.status === 'accepted');
  const hasReviewed = (uid) => myReviewees.includes(uid);
  const dateStr = event.event_date ? new Date(event.event_date).toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US',
    { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="event-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/events')} className="flex items-center gap-1 text-[#5C5C5C] hover:text-[#D97736] mb-6 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>

        <div className="bg-white border border-[#E5E4E0] rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0">{cat ? cat[language] || cat.lv : event.category}</Badge>
            {(isOrganizer || user?.role === 'admin') && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/events/${id}/edit`)}
                className="ml-auto border-[#E5E4E0] text-[#4A6B53] gap-1" data-testid="edit-event-btn">
                <Pencil className="w-3.5 h-3.5" /> {tr('Rediģēt', 'Edit', 'Изменить')}
              </Button>
            )}
            <Badge className={`${status.color} text-white border-0 ${(isOrganizer || user?.role === 'admin') ? '' : 'ml-auto'}`} data-testid="event-status-badge">{status[language] || status.lv}</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold font-outfit text-[#121212] mb-3">{event.title}</h1>
          {event.description && <p className="text-[#5C5C5C] font-figtree leading-relaxed whitespace-pre-line">{event.description}</p>}
          <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#F3F2EE]">
            <div className="flex items-center gap-2 text-sm text-[#5C5C5C]"><MapPin className="w-4 h-4 text-[#D97736]" />{event.location_name || event.city || event.region}</div>
            {event.address && <div className="flex items-center gap-2 text-sm text-[#5C5C5C]"><MapPin className="w-4 h-4 text-[#5C5C5C]" />{event.address}</div>}
            {dateStr && <div className="flex items-center gap-2 text-sm text-[#5C5C5C]"><CalendarDays className="w-4 h-4 text-[#4A6B53]" />{dateStr}</div>}
          </div>
          {event.lat != null && event.lng != null && (
            <div className="mt-6">
              <MiniLocationMap lat={event.lat} lng={event.lng} height={200}
                label={tr('Norises vieta', 'Event location', 'Место проведения')} />
            </div>
          )}
          {(() => {
            const dest = (event.lat != null && event.lng != null)
              ? `${event.lat},${event.lng}`
              : (event.address || [event.location_name, event.city].filter(Boolean).join(', '));
            if (!dest) return null;
            const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
            return (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {(event.address || event.location_name) && (
                  <span className="flex items-center gap-1.5 text-sm text-[#5C5C5C]" data-testid="event-address-line">
                    <MapPin className="w-4 h-4 text-[#D97736]" />
                    {event.address || `${event.location_name}${event.city ? ', ' + event.city : ''}`}
                  </span>
                )}
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A6B53] hover:text-[#3d5944] hover:underline"
                   data-testid="event-directions-link">
                  <Navigation className="w-4 h-4" /> {tr('Norādes (Google Maps)', 'Directions (Google Maps)', 'Маршрут (Google Maps)')}
                </a>
              </div>
            );
          })()}
          <p className="text-xs text-[#9CA3AF] mt-3">{tr('Organizators', 'Organizer', 'Организатор')}: {event.organizer_name}</p>

          {event.event_date && event.status === 'open' && (
            <div className="mt-4 pt-4 border-t border-[#F3F2EE]" data-testid="event-calendar-cta">
              <AddToCalendar event={event} attending={attending} onChange={setAttending} />
            </div>
          )}

          {isOrganizer && event.status === 'open' && (
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={completeEvent} disabled={busy}
                className="bg-[#4A6B53] hover:bg-[#3d5944] text-white" data-testid="complete-event-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> {tr('Pabeigt pasākumu', 'Complete event', 'Завершить')}
              </Button>
              <Button variant="outline" onClick={async () => { if (window.confirm(tr('Atcelt pasākumu?', 'Cancel event?', 'Отменить?'))) { await axios.post(`${API}/events/${id}/cancel`, {}, { withCredentials: true }); load(); } }}
                className="border-red-200 text-red-600 hover:bg-red-50" data-testid="cancel-event-btn">
                <X className="w-4 h-4 mr-1" /> {tr('Atcelt pasākumu', 'Cancel event', 'Отменить')}
              </Button>
            </div>
          )}
        </div>

        {/* Slots */}
        <div className="bg-white border border-[#E5E4E0] rounded-xl p-6">
          {event.team_complete && event.status === 'open' && (
            <div className="flex items-center gap-2 bg-green-50 text-[#4A6B53] rounded-lg p-3 mb-4 text-sm font-medium" data-testid="team-complete-banner">
              <Check className="w-4 h-4" /> {tr('Komanda ir nokomplektēta — visas lomas aizpildītas!', 'The team is complete — all roles filled!', 'Команда укомплектована — все роли заполнены!')}
            </div>
          )}
          <h2 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-[#4A6B53]" />{tr('Lomas', 'Roles', 'Роли')}</h2>
          <div className="space-y-3" data-testid="event-slots">
            {(event.slots || []).map((slot) => {
              const full = slot.count_filled >= slot.count_needed;
              const myApp = myAppFor(slot.id);
              const slotApps = applications.filter((a) => a.slot_id === slot.id);
              return (
                <div key={slot.id} className="p-4 rounded-lg border border-[#F3F2EE]" data-testid={`slot-${slot.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-[#121212]">{slot.role_title}</p>
                      <p className="text-xs text-[#5C5C5C]">
                        {slot.count_filled}/{slot.count_needed} {tr('aizpildīts', 'filled', 'заполнено')}
                        {slot.pay != null && ` · €${slot.pay}`}
                      </p>
                    </div>
                    {!isOrganizer && event.status === 'open' && (
                      myApp ? (
                        <Badge className={`${APP_STATUS[myApp.status].cls} border-0`} data-testid={`my-app-status-${slot.id}`}>{APP_STATUS[myApp.status][language] || APP_STATUS[myApp.status].lv}</Badge>
                      ) : full ? (
                        <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0">{tr('Aizpildīts', 'Full', 'Заполнено')}</Badge>
                      ) : (
                        <Button size="sm" onClick={() => openApply(slot)} className="bg-[#4A6B53] hover:bg-[#3d5944] text-white shrink-0" data-testid={`apply-btn-${slot.id}`}>
                          {tr('Pieteikties', 'Apply', 'Подать заявку')}
                        </Button>
                      )
                    )}
                  </div>

                  {/* Organizer: applications for this slot */}
                  {isOrganizer && slotApps.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#F3F2EE] space-y-2" data-testid={`slot-apps-${slot.id}`}>
                      {slotApps.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`application-${a.id}`}>
                          <Link to={`/pro/${a.applicant_id}`} className="font-medium text-[#121212] hover:text-[#D97736]">{a.applicant_name}</Link>
                          {a.message && <span className="text-[#5C5C5C] truncate flex-1">— {a.message}</span>}
                          {a.status === 'pending' ? (
                            <span className="flex gap-1 ml-auto shrink-0">
                              <button onClick={() => decide(a.id, true)} className="p-1.5 rounded-full bg-green-100 hover:bg-green-200" data-testid={`accept-app-${a.id}`}><Check className="w-4 h-4 text-[#4A6B53]" /></button>
                              <button onClick={() => decide(a.id, false)} className="p-1.5 rounded-full bg-red-100 hover:bg-red-200" data-testid={`reject-app-${a.id}`}><X className="w-4 h-4 text-red-600" /></button>
                            </span>
                          ) : (
                            <Badge className={`${APP_STATUS[a.status].cls} border-0 ml-auto shrink-0`}>{APP_STATUS[a.status][language] || APP_STATUS[a.status].lv}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {!user && (
            <p className="text-xs text-[#5C5C5C] mt-4 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />{tr('Pieslēdzies, lai pieteiktos lomām.', 'Log in to apply for roles.', 'Войдите, чтобы подать заявку.')}</p>
          )}
        </div>

        {/* Reviews after completion */}
        {event.status === 'completed' && (
          <div className="bg-white border border-[#E5E4E0] rounded-xl p-6 mt-6" data-testid="event-reviews-section">
            <h2 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-[#D97736]" />{tr('Atsauksmes', 'Reviews', 'Отзывы')}</h2>

            {/* Participant rates the organizer */}
            {iAmAccepted && !isOrganizer && (
              hasReviewed(event.organizer_id) ? (
                <div className="flex items-center gap-2 bg-[#F3F2EE] text-[#5C5C5C] rounded-lg p-3 mb-4 text-sm" data-testid="organizer-reviewed-msg">
                  <Star className="w-4 h-4 fill-[#D97736] text-[#D97736]" /> {tr('Tu jau novērtēji organizatoru', 'You already rated the organizer', 'Вы уже оценили')}
                </div>
              ) : (
                <Button onClick={() => openReview(event.organizer_id, event.organizer_name)}
                  className="bg-[#D97736] hover:bg-[#C06528] text-white mb-4" data-testid="rate-organizer-btn">
                  <Star className="w-4 h-4 mr-1" /> {tr('Novērtē organizatoru', 'Rate the organizer', 'Оценить организатора')}
                </Button>
              )
            )}

            {/* Organizer rates accepted participants */}
            {isOrganizer && acceptedApps.length > 0 && (
              <div className="mb-4 space-y-2" data-testid="rate-participants">
                <p className="text-sm text-[#5C5C5C]">{tr('Novērtē dalībniekus', 'Rate participants', 'Оцените участников')}</p>
                {acceptedApps.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-[#F3F2EE]">
                    <span className="text-sm font-medium text-[#121212]">{a.applicant_name}</span>
                    {hasReviewed(a.applicant_id) ? (
                      <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0">{tr('Novērtēts', 'Rated', 'Оценён')}</Badge>
                    ) : (
                      <Button size="sm" onClick={() => openReview(a.applicant_id, a.applicant_name)}
                        className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid={`rate-participant-${a.applicant_id}`}>
                        <Star className="w-4 h-4 mr-1" /> {tr('Novērtēt', 'Rate', 'Оценить')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {reviews.length === 0 ? (
              <p className="text-sm text-[#5C5C5C]" data-testid="no-reviews">{tr('Vēl nav atsauksmju.', 'No reviews yet.', 'Отзывов пока нет.')}</p>
            ) : (
              <div className="space-y-3" data-testid="event-reviews-list">
                {reviews.map((r) => (
                  <div key={r.id} className="p-3 rounded-lg border border-[#F3F2EE]" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-[#121212]">{r.reviewer_name}</span>
                      <span className="text-xs text-[#9CA3AF]">→ {r.reviewee_name}</span>
                      <span className="flex items-center gap-0.5 ml-auto">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-3.5 h-3.5 ${r.rating >= s ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                        ))}
                      </span>
                    </div>
                    {r.comment && <p className="text-sm text-[#5C5C5C]">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {reviewTarget && (
        <EventReviewDialog
          eventId={id}
          revieweeId={reviewTarget.id}
          revieweeName={reviewTarget.name}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          onReviewed={() => { setReviewTarget(null); load(); }}
        />
      )}

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent data-testid="apply-dialog">
          <DialogHeader><DialogTitle className="font-outfit">{tr('Pieteikties lomai', 'Apply for role', 'Подать заявку')}: {applySlot?.role_title}</DialogTitle></DialogHeader>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
            placeholder={tr('Pastāsti, kāpēc esi piemērots šai lomai...', 'Tell why you fit this role...', 'Расскажите, почему вы подходите...')} data-testid="apply-message-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)} className="border-[#E5E4E0] text-[#5C5C5C]">{tr('Atcelt', 'Cancel', 'Отмена')}</Button>
            <Button onClick={submitApply} disabled={busy} className="bg-[#4A6B53] hover:bg-[#3d5944] text-white" data-testid="submit-apply-btn">
              {busy ? tr('Sūta...', 'Sending...', 'Отправка...') : tr('Pieteikties', 'Apply', 'Подать')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
